import React from "react";
import { Link } from "gatsby";
import { 
  ChevronRight, MapPin, Globe,
  Facebook, Instagram, Youtube, Send, MessageCircle 
} from "lucide-react";
import { TikTok } from "./icons/TikTok";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-black text-gray-400 pt-16 pb-8 overflow-hidden font-sans border-t border-white/5">
      {/* Background Glow */}
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-brand-primary/5 rounded-full blur-[120px] pointer-events-none -z-10"></div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        
        {/* MAIN GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-12">
          
          {/* COLUMN 1: BRAND */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-brand-primary rounded-full shadow-[0_0_12px_rgba(92,131,116,0.8)]"></div>
              <span className="text-xl uppercase tracking-[0.2em] font-semibold text-white">Khalifa Niyas</span>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
              Empowering the next generation of ICT leaders with exam-ready, industry-aware education.
            </p>
          </div>

          {/* COLUMN 2: QUICK LINKS */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {[
                { label: "Home", to: "/" },
                { label: "About", to: "/about" },
                { label: "Timetable", to: "/timetable" },
                { label: "Contact", to: "/contact" },
                 { label: "Privacy Policy", to: "/privacy-policy" },
              ].map((link, idx) => (
                <li key={idx}>
                  <Link 
                    to={link.to} 
                    className="text-gray-500 hover:text-brand-primary transition-colors text-sm flex items-center gap-2 group"
                  >
                    <ChevronRight size={14} className="text-brand-primary/50 group-hover:text-brand-primary transition-colors" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* COLUMN 3: CENTERS */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Our Centers
            </h3>
            <ul className="space-y-4">
              {[
                { name: "Maxicon", loc: "Kiribathgoda" },
                { name: "Sigma", loc: "Kirulapone" },
                { name: "Farade", loc: "Kandy" },
                { name: "Sasik", loc: "Gampaha" },
                { name: "Online", loc: "Global Batch" },
              ].map((center, idx) => (
                <li key={idx} className="flex items-start gap-3">
                   <div className="mt-1 w-4 h-4 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary shrink-0">
                      {center.name === 'Online' ? <Globe size={10} /> : <MapPin size={10} />}
                   </div>
                   <div>
                     <span className="text-sm font-medium text-gray-300 block">{center.name}</span>
                     <span className="text-xs text-gray-500 block">{center.loc}</span>
                   </div>
                </li>
              ))}
            </ul>
          </div>

          {/* COLUMN 4: CONNECT */}
          <div id="contact">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Connect
            </h3>
            <div className="flex flex-wrap gap-3">
              {[
                { icon: Facebook, href: "https://www.facebook.com/khalifa.niyas/" },
                { icon: Instagram, href: "https://www.instagram.com/khalifa___niyas/" },
                { icon: Youtube, href: "https://www.youtube.com/@khalifaniyas5193" },
                { icon: TikTok, href: "https://www.tiktok.com/@khalifa___niyas" }
              ].map((social, idx) => {
                const Icon = social.icon;
                return (
                  <a 
                    key={idx} 
                    href={social.href} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-all border border-white/5 hover:border-brand-primary/30 group"
                  >
                    <Icon size={18} className="group-hover:scale-110 transition-transform" />
                  </a>
                );
              })}
            </div>
            <p className="text-xs text-gray-600 mt-4 max-w-[200px]">
              Follow for daily updates and exam tips.
            </p>
          </div>
        </div>

        {/* BOTTOM */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-600">
          <p>© {currentYear} Khalifa Niyas. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
